// src/hooks/useCachingMonitor.ts
import { useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from '@tanstack/react-query'

import useCompanyPageBatchResults from '@/queries/message/useCompanyPageBatchResults'
import { toast } from '@/hooks/useToast'
import {
  ALL_COMPANY_PAGE_WORKFLOWS,
  COMPANY_PAGE_CACHE_TRACKING_KEY,
  CompanyPageCachingTrackingItem,
} from '@/constants/company_page'
import MessageQueryKeyGenerator from '@/queries/message/MessageQueryKeyGenerator'

interface UseTrackCompanyPageCacheProps {
  messageId: string
  quarter: number
  year: number
  enabled: boolean
  lang?: string
  onComplete?: () => void
}

export const useTrackCompanyPageCache = ({
  messageId,
  quarter,
  year,
  enabled,
  lang,
  onComplete,
}: UseTrackCompanyPageCacheProps) => {
  const [isMonitoring, setIsMonitoring] = useState(enabled)
  const hasPolledOnceRef = useRef(false)
  const completedRef = useRef(false)
  const router = useRouter()
  const queryClient = useQueryClient()
  /* 동일 회사에 대해 여러번 요청이 가는 것을 방지하기 위한 부분 */
  const getCurrentStockId = () => {
    const stored = localStorage.getItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
    if (!stored) return null
    const items: CompanyPageCachingTrackingItem[] = JSON.parse(stored)
    const currentItem = items.find((item) => item.messageId === messageId)
    return currentItem?.stockId || null
  }

  const getMasterTrackingItem = () => {
    const stored = localStorage.getItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
    if (!stored) return null

    const items: CompanyPageCachingTrackingItem[] = JSON.parse(stored)
    const currentItem = items.find((item) => item.messageId === messageId)
    if (!currentItem) return null

    const stockId = currentItem.stockId

    // 같은 stockId, quarter, year를 가진 첫 번째 아이템을 master로 사용
    const masterItem = items.find(
      (item) => item.stockId === stockId && item.quarter === quarter && item.year === year
    )

    return masterItem
  }

  const isMasterMessage = () => {
    const masterItem = getMasterTrackingItem()
    return masterItem?.messageId === messageId
  }

  const masterMessageId = getMasterTrackingItem()?.messageId || messageId

  const {
    data: batchResults,
    refetch,
    error,
  } = useCompanyPageBatchResults({ id: masterMessageId, quarter, year, lang })

  // 하루 이상 지난 데이터인지 체크하는 함수
  const isDataExpired = () => {
    const stored = localStorage.getItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
    if (!stored) return false

    const items: CompanyPageCachingTrackingItem[] = JSON.parse(stored)
    const currentItem = items.find((item) => item.messageId === messageId)

    if (!currentItem) return false

    const startedAt = new Date(currentItem.startedAt)
    const now = new Date()
    const hoursDiff = (now.getTime() - startedAt.getTime()) / (1000 * 60 * 60)

    return hoursDiff >= 24
  }

  // 캐싱 완료 여부 체크
  const checkCachingComplete = () => {
    if (!batchResults) return false

    const allSections = ALL_COMPANY_PAGE_WORKFLOWS
    if (allSections.length === 0) return false

    return allSections.every((key) => {
      const section = batchResults[key]
      if (!section || section.status === 'pending') return false
      return section.status === 'completed' || section.status === 'failed'
    })
  }

  const getCachingProgress = () => {
    if (!batchResults) return { completed: 0, total: 0, percentage: 0 }

    const allSections = ALL_COMPANY_PAGE_WORKFLOWS
    const total = allSections.length
    const completed = allSections.filter((key) => {
      const section = batchResults[key]
      return section && (section.status === 'completed' || section.status === 'failed')
    }).length

    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    }
  }

  const removeFromTracking = () => {
    const stored = localStorage.getItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
    if (!stored) return

    const items: CompanyPageCachingTrackingItem[] = JSON.parse(stored)
    const filtered = items.filter((item) => item.messageId !== messageId)

    if (filtered.length > 0) {
      localStorage.setItem(COMPANY_PAGE_CACHE_TRACKING_KEY, JSON.stringify(filtered))
    } else {
      localStorage.removeItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
    }
  }

  useEffect(() => {
    // master messageId만 폴링하도록 변경
    if (!isMonitoring || completedRef.current || !isMasterMessage()) return

    const interval = setInterval(() => {
      refetch({ cancelRefetch: false })
    }, 25000)

    return () => clearInterval(interval)
  }, [isMonitoring, refetch])

  useEffect(() => {
    if (!isMonitoring || completedRef.current) return

    const isComplete = checkCachingComplete()

    if (batchResults) {
      hasPolledOnceRef.current = true
    }

    if (isComplete) {
      completedRef.current = true
      setIsMonitoring(false)

      const stored = localStorage.getItem(COMPANY_PAGE_CACHE_TRACKING_KEY)

      if (stored) {
        const items: CompanyPageCachingTrackingItem[] = JSON.parse(stored)
        const currentStockId = getCurrentStockId()

        // 같은 stockId + quarter + year를 가진 모든 아이템 처리
        const relatedItems = items.filter(
          (item) =>
            item.stockId === currentStockId && item.quarter === quarter && item.year === year
        )

        // 모든 관련 아이템에 대해 처리
        relatedItems.forEach((item) => {
          const tabViewKey = `company_page_tab_viewed_${item.messageId}`
          if (!localStorage.getItem(tabViewKey)) {
            localStorage.setItem(tabViewKey, JSON.stringify([]))
          }

          const completionFlagKey = `company_page_completion_flag_${item.messageId}`
          localStorage.setItem(completionFlagKey, 'true')

          queryClient.removeQueries({
            queryKey: MessageQueryKeyGenerator.company_page_batch_results(
              item.messageId,
              quarter,
              year
            ),
          })

          // 각 conversation에 대한 toast 표시
          toast({
            type: 'alarm',
            text: `Company DeepDive for ${item.stockName} has been generated.`,
            duration: 500000,
            onClick: () => {
              if (!item.conversationId) return
              router.push(`/tfs_search?conversation_id=${item.conversationId}`)
            },
          })
        })

        // 모든 관련 아이템을 tracking에서 제거
        const filtered = items.filter(
          (item) =>
            !(item.stockId === currentStockId && item.quarter === quarter && item.year === year)
        )

        if (filtered.length > 0) {
          localStorage.setItem(COMPANY_PAGE_CACHE_TRACKING_KEY, JSON.stringify(filtered))
        } else {
          localStorage.removeItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
        }
      }

      onComplete?.()
    } else if (hasPolledOnceRef.current && isDataExpired()) {
      // 최소 한 번 폴링했고, 완료되지 않았으며, 하루 이상 지났다면 제거
      completedRef.current = true
      setIsMonitoring(false)
      removeFromTracking()
    }
  }, [batchResults, isMonitoring, messageId, onComplete])

  useEffect(() => {
    if (enabled && !completedRef.current) {
      setIsMonitoring(true)
    }
  }, [enabled])

  // Reset polling when lang changes so new language generation is tracked
  useEffect(() => {
    completedRef.current = false
    hasPolledOnceRef.current = false
    setIsMonitoring(true)
  }, [lang])

  // 404 에러 발생 시 캐싱 아이템 삭제 (안전장치)
  useEffect(() => {
    // AxiosError의 status가 404인 경우
    if (error && (error as any).status === 404) {
      const stored = localStorage.getItem(COMPANY_PAGE_CACHE_TRACKING_KEY)
      if (!stored) return

      const items: CompanyPageCachingTrackingItem[] = JSON.parse(stored)
      const hasItem = items.some((item) => item.messageId === messageId)

      if (hasItem) {
        removeFromTracking()
        setIsMonitoring(false)
        completedRef.current = true
      }
    }
  }, [error, messageId])

  return {
    isMonitoring,
    isCachingComplete: checkCachingComplete(),
    progress: getCachingProgress(),
    batchResults,
  }
}
